import { useEffect, useRef } from 'react';

/**
 * Sets --app-vh CSS variable based on visualViewport height.
 * Fixes WebView keyboard issues where 100vh doesn't update.
 *
 * During orientation changes the viewport fires many intermediate resize
 * events (e.g. 896→600→414). To avoid a visible "shrink then grow" effect,
 * resize events are debounced while an orientation change is in progress.
 */
export function useWebViewViewportHeight() {
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orientationChanging = useRef(false);

  useEffect(() => {
    const setVh = (vh: number) => {
      document.documentElement.style.setProperty('--app-vh', `${vh}px`);
    };

    const getVh = () =>
      window.visualViewport ? window.visualViewport.height : window.innerHeight;

    const updateVhImmediate = () => {
      if (orientationChanging.current) return; // skip intermediate values
      setVh(getVh());
    };

    const updateVhDebounced = () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        setVh(getVh());
        debounceTimer.current = null;
      }, 120);
    };

    const handleOrientationChange = () => {
      // Mark orientation as changing — suppress intermediate resize values
      orientationChanging.current = true;

      // After the rotation animation settles, commit the final value.
      // iOS rotation animation takes ~300-400ms; we wait 500ms to ensure
      // the viewport has fully settled before reading dimensions.
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        orientationChanging.current = false;
        setVh(getVh());
        debounceTimer.current = null;
      }, 500);
    };

    // Initial set
    setVh(getVh());

    // Keyboard open/close (visualViewport) — update immediately
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateVhDebounced);
      window.visualViewport.addEventListener('scroll', updateVhImmediate);
    }
    // Resize (may fire during orientation change) — debounced
    window.addEventListener('resize', updateVhDebounced);
    // Orientation change — debounce until animation settles
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateVhDebounced);
        window.visualViewport.removeEventListener('scroll', updateVhImmediate);
      }
      window.removeEventListener('resize', updateVhDebounced);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);
}
